import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getExpenseCategoryById, createExpenseCategory, updateExpenseCategory, getChartOfAccounts } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import EntityTranslationsButton from "../components/UI/EntityTranslationsButton";
import { useTranslation } from "../context/I18nContext";

export default function AddEditExpenseCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("accounting");

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ name: "", default_account_id: "", is_active: true });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getChartOfAccounts(token).then((all) => setAccounts(all.filter((a) => a.type === "expense"))).catch(() => {});

    if (id) {
      getExpenseCategoryById(token, id).then((cat) => {
        setForm({
          name: cat.name || "",
          default_account_id: cat.default_account_id ? String(cat.default_account_id) : "",
          is_active: cat.is_active !== undefined ? cat.is_active : true,
        });
      }).catch(() => notify.error(t("notify_load_category_failed", "Failed to load expense category")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountOptions = [
    { value: "", label: t("default_account_none_option", "Use the default expense account") },
    ...accounts.map((a) => ({ value: String(a.id), label: `${a.code} — ${a.name}` })),
  ];

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_category_name_required", "Category name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      default_account_id: form.default_account_id ? Number(form.default_account_id) : 0,
      is_active: form.is_active,
    };

    try {
      if (id) {
        await updateExpenseCategory(token, id, payload);
        notify.success(t("notify_category_updated", "Expense category updated successfully"));
      } else {
        await createExpenseCategory(token, payload);
        notify.success(t("notify_category_created", "Expense category created successfully"));
      }
      navigate("/expense-categories");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/expense-categories")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("category_edit_title", "Edit Expense Category") : t("category_add_title", "Add Expense Category")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("category_form_subtitle", "Optionally pick which account expenses in this category post to")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <FloatingInput label={t("field_category_name_label", "Category Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.ExpenseCategories} />
            </div>
            <EntityTranslationsButton entityType="expense_category" entityId={id} defaultName={form.name} />
          </div>

          <SelectBox
            label={t("field_default_account_label", "Default Account (optional)")}
            options={accountOptions}
            value={form.default_account_id}
            onChange={(val) => setForm((prev) => ({ ...prev, default_account_id: val }))}
          />

          <ToggleSwitch
            label={tCommon("status_active", "Active")}
            checked={form.is_active}
            onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_category", "Update Category") : t("btn_create_category", "Create Category")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/expense-categories")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
